<template>
  <div v-if="skeleton">
    <price
      text=""
      :price="0"
      skeleton
      style="width: 100px; margin-left: auto; margin-right: auto"
    />
  </div>
  <div
    v-else
    class="product-price d-flex justify-center"
    :class="{ 'product-price--selectable': selectable }"
    itemprop="offers"
    itemscope
    itemtype="https://schema.org/Offer"
    data-cy-product-price
  >
    <meta itemprop="priceCurrency" :content="currency" />

    <link
      v-if="available"
      itemprop="availability"
      href="https://schema.org/InStock"
    />
    <link v-else itemprop="availability" href="https://schema.org/OutOfStock" />

    <div v-if="selectable">
      <nuxt-link
        :to="links.preco"
        class="square d-flex align-center"
        :class="{
          active: currentProductStatus === STATUS.PRECOMMANDE,
          loading: loadingOtherProduct,
        }"
      >
        <i class="product-price__input"></i>
        <div class="product-price__info flex-grow-1">
          <p class="product-price__info__title text-secondary mb-0-2">
            {{ $t('purchase.price.preco') }}
          </p>
          <p class="text-small mb-0">
            <transition name="fade">
              <span v-if="loadingOtherProduct || onRefresh" class="d-block">
                <span
                  class="skeleton d-block mb-0-2"
                  style="height: 15px"
                ></span>
              </span>
              <span v-else>
                {{ $t('purchase.estimatedDelivery') }}
                <strong class="w-600">{{
                  currentProductStatus === STATUS.STOCK && otherProduct
                    ? $t(otherDeliveryText.ref, otherDeliveryText.options)
                    : $t(deliveryText.ref, deliveryText.options)
                }}</strong>
              </span>
            </transition>
          </p>
        </div>
        <price
          :price="
            currentProductStatus === STATUS.STOCK && otherProduct
              ? parseInt(otherProduct.priceRange.minVariantPrice.amount)
              : price
          "
          class="text-md-center"
        />
      </nuxt-link>
    </div>
    <div
      v-else-if="price !== compareAtPrice"
      class="product-price__compare-at-price d-flex justify-end"
      data-cy-price
    >
      <price
        :text="$t('purchase.price.stock')"
        :compare="true"
        :price="compareAtPrice"
        class="text-md-center"
      />
    </div>

    <div v-if="selectable">
      <nuxt-link
        :to="links.stock"
        class="square d-flex align-center"
        :class="{
          active: currentProductStatus === STATUS.STOCK,
          loading: loadingOtherProduct,
        }"
      >
        <i class="product-price__input"></i>
        <div class="product-price__info flex-grow-1">
          <p class="product-price__info__title text-secondary mb-0-2">
            {{ $t('purchase.price.stock') }}
          </p>
          <p class="text-small mb-0">
            <transition name="fade">
              <span v-if="loadingOtherProduct || onRefresh">
                <span
                  class="skeleton d-block mb-0-2"
                  style="height: 15px"
                ></span>
              </span>
              <span v-else class="d-block">
                {{ $t('purchase.delivery') }}
                <strong class="w-600">{{
                  currentProductStatus === STATUS.PRECOMMANDE && otherProduct
                    ? $t(otherDeliveryText.ref, otherDeliveryText.options)
                    : $t(deliveryText.ref, deliveryText.options)
                }}</strong>
              </span>
            </transition>
          </p>
        </div>
        <price
          :price="
            currentProductStatus === STATUS.PRECOMMANDE && otherProduct
              ? parseInt(
                  otherProduct.compareAtPriceRange.minVariantPrice.amount
                )
              : compareAtPrice
          "
          class="text-md-center"
        />
      </nuxt-link>
    </div>
    <div
      v-else
      class="d-flex product-price__price"
      :class="price === compareAtPrice ? 'justify-center' : 'justify-start'"
      data-cy-price
    >
      <price
        :text="
          price !== compareAtPrice
            ? $t('purchase.price.preco')
            : $t('purchase.price.stock')
        "
        :price="price"
        class="text-md-center"
      />
    </div>

    <p
      v-if="showStockPrecoExplain"
      ref="tooltip"
      class="link--underline text-extra-small mt-0-4 mb-0 tooltip"
    >
      <span>{{ $t('purchase.stock_preco_why') }}</span>
    </p>
  </div>
</template>

<script>
import { isWithinInterval } from 'date-fns'
import { toHTML } from '@portabletext/to-html'

import services from '@/services'

import Price from '@/components/Price/Price'

import { STATUS } from '@/const'

import DeliveryMixin from '@/mixins/deliveryText'

export default {
  name: 'ProductPrice',
  components: { Price },
  mixins: [DeliveryMixin],
  props: {
    price: { type: Number, required: true },
    compareAtPrice: { type: Number, required: true },
    available: { type: Boolean, default: false },
    currency: { type: String, default: '€' },
    selectable: { type: Boolean, default: false },
    productDocument: { type: Object, default: null },
    productStatus: { type: String, default: null },
    shopifyProduct: { type: Object, default: null },
    selectedColor: { type: String, default: '' },
    onRefresh: { type: Boolean, default: false },
    skeleton: { type: Boolean, default: false },
  },
  data: () => ({ otherProduct: null, loadingOtherProduct: false, STATUS }),
  async fetch() {
    if (this.selectable) {
      await this.loadOtherProduct()
    }
  },
  computed: {
    currentProductStatus() {
      return this.productStatus || this.productDocument.status
    },
    links() {
      let preco, stock

      if (this.currentProductStatus === STATUS.PRECOMMANDE) {
        preco = this.$contextSanityPath(this.productDocument)
        stock = this.$contextSanityPath(
          this.productDocument.product_as_preco_or_stock
        )
      } else {
        stock = this.$contextSanityPath(this.productDocument)
        preco = this.$contextSanityPath(
          this.productDocument.product_as_preco_or_stock
        )
      }

      return {
        preco: {
          path: preco,
          query: {
            color: this.selectedColor ? this.selectedColor : undefined,
          },
        },
        stock: {
          path: stock,
          query: {
            color: this.selectedColor ? this.selectedColor : undefined,
          },
        },
      }
    },

    deliveryText() {
      if (
        this.currentProductStatus === STATUS.PRECOMMANDE &&
        this.shopifyProduct.metafield
      ) {
        const d = new Date(this.shopifyProduct.metafield)
        if (!isNaN(d.getTime())) {
          return this.getDeliveryFromDate(d)
        }
      }

      if (this.currentProductStatus === STATUS.STOCK) {
        return { ref: 'purchase.fewDays', options: {} }
      }

      return { ref: 'purchase.noDays', options: {} }
    },

    otherDeliveryText() {
      if (this.otherProduct) {
        const delivery =
          this.otherProduct.metafield && this.otherProduct.metafield.value
            ? new Date(this.otherProduct.metafield.value)
            : undefined

        if (
          this.otherProduct.status === STATUS.PRECOMMANDE &&
          delivery &&
          !isNaN(delivery.getTime())
        ) {
          return this.getDeliveryFromDate(delivery)
        }
        if (this.otherProduct.status === STATUS.STOCK) {
          return { ref: 'purchase.fewDays', options: {} }
        }
      }

      return { ref: 'purchase.noDays', options: {} }
    },

    showStockPrecoExplain() {
      return this.selectable && process.env.STOCK_PRECO_BIBLIO_UID
    },
  },
  mounted() {
    if (!this.$fetchState.pending) {
      if (this.selectable) {
        this.loadOtherProduct()
      }
    }

    if (this.showStockPrecoExplain) {
      this.initTooltip()
    }
  },
  methods: {
    async loadOtherProduct() {
      try {
        this.loadingOtherProduct = true

        if (this.productDocument.product_as_preco_or_stock.shopify_product.id) {
          this.otherProduct =
            await services.productService.getShopifyProductPriceAndMetafield(
              this.productDocument.product_as_preco_or_stock.shopify_product.id,
              this.$i18n.locale
            )

          let status = this.productDocument.product_as_preco_or_stock.status
          if (this.otherProduct.metafields) {
            let startDate = this.otherProduct.metafields.find(
              (m) => m && m.key === 'start_date_web'
            )?.value

            let endDate = this.otherProduct.metafields.find(
              (m) => m && m.key === 'start_date_web'
            )?.value

            if (startDate && endDate) {
              startDate = new Date(startDate)
              endDate = new Date(endDate)

              if (
                isWithinInterval(new Date(), { start: startDate, end: endDate })
              ) {
                status = STATUS.STOCK
              }
            }
          }

          this.otherProduct.status = status
        }

        this.loadingOtherProduct = false
      } catch (err) {
        this.$logError.captureException(err)
      }
    },
    async initTooltip() {
      if (this.$refs.tooltip) {
        const tippy = await import('tippy.js' /* webpackChunkName: 'tippy' */)
        await import(
          '@/scss/09-libs/_06-tippy.scss' /* webpackChunkName: 'tippy_css' */
        )
        await import(
          'tippy.js/animations/scale.css' /* webpackChunkName: 'tippy_animation_scale_css' */
        )

        tippy.default(this.$refs.tooltip, {
          theme: 'dark',
          placement: 'top',
          animation: 'scale',
          content: this.$t('loading'),
          allowHTML: true,
          arrow: true,
          onShow: (instance) => {
            services.sanityService
              .getBiblio(process.env.STOCK_PRECO_BIBLIO_UID)

              .then((biblio) => {
                if (biblio) {
                  instance.setContent(toHTML(biblio.explication))
                } else {
                  instance.setContent(this.$t('request.no-result'))
                }
              })
              .catch((err) => {
                instance.setContent(this.$t('request.failed'))
                this.$logError.captureException(err)
              })
          },
        })
      }
    },
  },
}
</script>

<style lang="scss">
.product-price {
  > div {
    margin: 0 calc(var(--spacing) * 0.5);

    @include mq($from: tablet) {
      width: 50%;
    }
  }

  .square {
    border: var(--border-width) solid var(--secondary-color-4);
    border-bottom-width: 0;
    padding: var(--spacing) calc(var(--spacing) * 0.8);
    margin: 0 auto;

    &:last-child,
    &.active {
      border-bottom-width: var(--border-width);
    }

    &.active {
      border-color: var(--secondary-color);
    }

    &:hover {
      .product-price__input {
        &::before {
          --circle-position: 0;

          box-shadow: inset 0 0 0 2px var(--secondary-color);
        }
      }
    }
  }

  &__input {
    --input-width: #{rem(36px)};
    --inside-width: #{rem(24px)};

    display: block;
    position: relative;
    width: var(--input-width);
    height: var(--input-width);
    border-radius: 50%;
    flex-shrink: 0;

    &::before {
      --circle-position: calc((var(--input-width) - var(--inside-width)) / 2);

      content: '';
      position: absolute;
      inset: var(--circle-position);
      border-radius: 50%;
      transition: all 0.3s cubic-bezier(0.46, 0.01, 0.32, 1);
      box-shadow: inset 0 0 0 2px var(--secondary-color-2);
    }

    &::after {
      content: '';
      position: absolute;
      top: calc((var(--input-width) - var(--inside-width)) / 2);
      left: calc((var(--input-width) - var(--inside-width)) / 2);
      width: var(--inside-width);
      height: var(--inside-width);
      background: var(--secondary-color-2);
      border-radius: 50%;
    }

    .active & {
      &::before {
        --circle-position: 0;

        box-shadow: inset 0 0 0 2px var(--secondary-color);
      }

      &::after {
        background: var(--secondary-color);
      }
    }
  }

  &--selectable {
    --border-width: #{rem(2px)};

    display: block;

    > div {
      width: auto;

      > p {
        width: auto;
        margin-left: auto;
        margin-right: auto;

        .tooltip {
          &::after {
            margin-left: 0;
          }
        }
      }

      + div {
        margin-top: calc(var(--border-width) * -1);
      }
    }

    .active {
      position: relative;
      z-index: 1;
    }

    .product-price__info {
      padding: 0 calc(var(--spacing) * 0.8);
      text-align: left;

      &__title {
        font-size: rem(18px);
        line-height: 1.05;
      }
    }

    .price__price {
      font-size: rem(28px);
      line-height: 1;
    }

    .product-price__price {
      order: 1;
    }

    .product-price__compare-at-price {
      order: 2;
    }
  }
}
</style>
